import React from "react";
import { View, Text, Switch, Platform} from "react-native";
import { Button } from "react-native-elements";
import { styles } from 'styles/screens/cloverConnectionReportsScreen';
import CustomSelect from 'components/CustomSelect';
import { IdentifierType, EntityType } from 'types/components/CustomSelectProps';
import { CloverPointConnectionStatusType } from "types/enums/CloverPointConnectionStatusType";
import { CloverPointConnectionType } from "types/enums/CloverPointConnectionType";
import { useFocusEffect } from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import Commerce from "types/models/Commerce";

export default ({ 
    handleSubmit,
    currentVenue, 
    isCommerceEditor, 
    setFieldValue, 
    values, 
    errors, 
    isSubmitting,
    mediumScreen,
    commerceCode,
    venueCode,
    connectionStatus
}: any): JSX.Element => {

    const [venueCodes, setVenueCodes] = React.useState<string[]>([]);
    const [commerceCodes, setCommerceCodes] = React.useState<string[]>([]);

    const { t } = useTranslation()
    
    const cloverStatuses = React.useMemo(() => {
        return {
            connectedClovers: CloverPointConnectionStatusType.CONNECTED,
            unstableClovers: CloverPointConnectionStatusType.UNSTABLE,
            disconnectedClovers: CloverPointConnectionStatusType.DISCONNECTED,
            inactiveClovers: CloverPointConnectionStatusType.INACTIVE,
        };
    }, []);

    const cloverConnectionTypes = React.useMemo(() => {
        return {
            ethernetConnection: CloverPointConnectionType.ETHERNET,
            wifiConnection: CloverPointConnectionType.WIFI,
            phoneConnection: CloverPointConnectionType.PHONE,
        };
    }, []);

    const handleToggle = (value: string, valueName: 'connectionStatuses' | 'connectionTypes') => {
        const currentValues = values[valueName] as string[];

        if (currentValues.includes(value)) {
            setFieldValue(valueName, currentValues.filter((item) => item !== value));
        } else {
            setFieldValue(valueName, [...currentValues, value]);
        }
    };

    useFocusEffect(
        React.useCallback(() => {
            if (!commerceCode) {
                setCommerceCodes([]);
                setFieldValue('codes', [...values.codes.filter((code: string) => code !== commerceCode)]);
            }

            if (venueCode && !commerceCode) {
                const commerceCodes = currentVenue.commerces.map((commerce: Commerce) => commerce.code);
                setVenueCodes([venueCode]);
                setCommerceCodes([...commerceCodes]);
                setFieldValue('codes', [, ...venueCode, ...commerceCodes]);
                setFieldValue('connectionStatuses', [connectionStatus ?? ''])
            }

            if (!commerceCode && !venueCode) {
                setFieldValue('connectionStatuses', []);
            }else{
                setFieldValue('codes', [commerceCode, venueCode].filter((code) => code));// No settea nulls, undefineds o ""              
                setFieldValue('connectionStatuses', [connectionStatus ?? '']);
            }

        }, [commerceCode, venueCode])
    );

    React.useEffect(() => {
        setFieldValue('codes', [...venueCodes, ...commerceCodes]);
    }, [venueCodes, commerceCodes]);

    React.useEffect(() => {
        if ((commerceCode || venueCode) && values.codes.length > 0) {
            handleSubmit()
        }
    }, [values.codes]);

    const venueItems = React.useMemo(
        () => (currentVenue ? [currentVenue] : []),
        [currentVenue]
    );

    return (
        <>
            <View style={{ width: '100%' }}>
                <Text style={styles.selectLabel}>{t('venues')}</Text>
                <CustomSelect
                    items={venueItems}
                    callback={(codes: string[]) => {
                        if (isCommerceEditor) {
                            setVenueCodes([]);
                        }else{
                            setVenueCodes(codes);
                        }
                    }}
                    isCommerceEditor={isCommerceEditor}
                    identifierType={IdentifierType.Code}
                    entityType={EntityType.Venue}
                    idOrCodesToPickFirst={venueCode}
                />
            </View>
            <View style={{ width: '100%' }}>
                <Text style={styles.selectLabel}>{t('commerces')}</Text>
                <CustomSelect
                    items={currentVenue?.commerces ?? []}
                    callback={(codes: any) => {
                        setCommerceCodes(codes);
                    }}
                    isCommerceEditor={isCommerceEditor}
                    identifierType={IdentifierType.Code}
                    entityType={EntityType.Commerce}
                    idOrCodesToPickFirst={commerceCode}
                />
            </View>
            {errors.codes && <Text style={styles.textError}>{errors.codes}</Text>}

            <View style={styles.switchesContainer }>
                
                {Object.keys(cloverStatuses).map((key) => {
                    const keyOf = key as keyof typeof cloverStatuses;
                    return (
                    <View key={keyOf} style={styles.switchContainer}>
                        <Text style={styles.switchTitle}>{t(`clover-connection-status.${keyOf}`)}</Text>
                        <Switch
                            trackColor={{ false: '#001639', true: currentVenue?.appColor }}
                            thumbColor={'#001639'}
                            onValueChange={() => handleToggle(cloverStatuses[keyOf], 'connectionStatuses')}
                            {...Platform.select({
                            web: {
                                activeThumbColor: currentVenue?.appColor,
                            },
                            })}
                            value={values.connectionStatuses.includes(cloverStatuses[keyOf])}
                            style={styles.switch}
                            disabled={isSubmitting}
                        />
                    </View>
                )})}                
                {Object.keys(cloverConnectionTypes).map((key) => {
                    const keyOf = key as keyof typeof cloverConnectionTypes;
                    return (
                    <View key={keyOf} style={styles.switchContainer}>
                        <Text style={styles.switchTitle}>{t(`clover-connection-status.${keyOf}`)}</Text>
                        <Switch
                            trackColor={{ false: '#001639', true: currentVenue?.appColor }}
                            thumbColor={'#001639'}
                            onValueChange={() => handleToggle(cloverConnectionTypes[keyOf], 'connectionTypes')}
                            {...Platform.select({
                            web: {
                                activeThumbColor: currentVenue?.appColor,
                            },
                            })}
                            value={values.connectionTypes.includes(cloverConnectionTypes[keyOf])}
                            style={styles.switch}
                            disabled={isSubmitting}
                        />
                    </View>
                )})}   
        </View>

        <View
            style={
                mediumScreen ? styles.registerOrderButtonContainerLarge : styles.registerOrderButtonContainerSmall
            }
        >
            <Button
                title={t('search')}
                type="solid"
                containerStyle={mediumScreen ? styles.orderBtnContainerLarge : styles.orderBtnContainerSmall}
                titleStyle={styles.btnTitleOrders}
                buttonStyle={mediumScreen ? styles.orderBtnLarge : styles.orderBtnSmall}
                onPress={() => {
                handleSubmit();
                }}
                disabled={isSubmitting || values.codes.length === 0}
            />
        </View>
        </>
    )
}